import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader, SectionCard } from '@striker/ui-shell';
import { apiFetch } from '../auth/session';

type TwilioStatus = {
  account_sid: string;
  messaging_service_sid: string;
  has_auth_token: boolean;
  credentials_populated: boolean;
  infrastructure_state: string;
  sms_otp_dev_bypass: boolean;
  updated_at: string | null;
};

const EMPTY: TwilioStatus = {
  account_sid: '',
  messaging_service_sid: '',
  has_auth_token: false,
  credentials_populated: false,
  infrastructure_state: 'NOT_CONFIGURED',
  sms_otp_dev_bypass: false,
  updated_at: null,
};

export function MasterTwilioPage() {
  const [status, setStatus] = useState<TwilioStatus>(EMPTY);
  const [accountSid, setAccountSid] = useState('');
  const [messagingSid, setMessagingSid] = useState('');
  const [authToken, setAuthToken] = useState('');
  const [tokenEdited, setTokenEdited] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setError(null);
    const res = await apiFetch('/api/v1/master/twilio');
    const body = (await res.json().catch(() => ({}))) as TwilioStatus & { error?: string; message?: string };
    if (!res.ok) {
      throw new Error(body.message || body.error || `HTTP ${res.status}`);
    }
    setStatus({
      account_sid: String(body.account_sid || ''),
      messaging_service_sid: String(body.messaging_service_sid || ''),
      has_auth_token: Boolean(body.has_auth_token),
      credentials_populated: Boolean(body.credentials_populated),
      infrastructure_state: String(body.infrastructure_state || 'NOT_CONFIGURED'),
      sms_otp_dev_bypass: Boolean(body.sms_otp_dev_bypass),
      updated_at: body.updated_at ? String(body.updated_at) : null,
    });
    setAccountSid(String(body.account_sid || ''));
    setMessagingSid(String(body.messaging_service_sid || ''));
    setAuthToken('');
    setTokenEdited(false);
  }

  useEffect(() => {
    void load().catch((err) => {
      setError(err instanceof Error ? err.message : String(err));
    });
  }, []);

  async function onSave() {
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      const payload: Record<string, string> = {
        account_sid: accountSid.trim(),
        messaging_service_sid: messagingSid.trim(),
      };
      if (tokenEdited && authToken.trim()) {
        payload.auth_token = authToken.trim();
      }
      const res = await apiFetch('/api/v1/master/twilio', {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
      if (!res.ok) {
        throw new Error(body.message || body.error || `HTTP ${res.status}`);
      }
      await load();
      setMessage('Impostazioni Twilio salvate (auth token mai restituito in chiaro).');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const mode = status.infrastructure_state;
  const modeTone =
    mode === 'LIVE' ? 'ok' : mode === 'SIMULATED_BYPASS' ? 'info' : 'warn';

  return (
    <section className="sk-mc-page">
      <PageHeader
        title="Twilio SMS"
        subtitle={
          <>
            <Link to="/master">← Master</Link> · credenziali per OTP prenotazioni pubbliche. Token
            cifrato in DB; wrap key = MASTER_EMAIL_SETTINGS_SECRET.
          </>
        }
        actions={
          <button type="button" className="sk-btn" disabled={busy} onClick={() => void onSave()}>
            {busy ? 'Salvataggio…' : 'Salva'}
          </button>
        }
      />

      {message ? <p className="sk-ok">{message}</p> : null}
      {error ? <p className="sk-error">{error}</p> : null}

      <div className="sk-theme-layout">
        <SectionCard title="Infrastruttura SMS">
          <p className="sk-box__label">Stato</p>
          <p className="sk-box__body">
            Modalità: <strong className={`sk-tone sk-tone--${modeTone}`}>{mode}</strong>
            {' · '}
            Credenziali: {status.credentials_populated ? 'popolate' : 'mancanti'}
            {status.sms_otp_dev_bypass ? ' · SMS_OTP_DEV_BYPASS=1' : null}
          </p>
          {status.updated_at ? (
            <p className="sk-muted">Aggiornato: {new Date(status.updated_at).toLocaleString()}</p>
          ) : null}
        </SectionCard>

        <SectionCard title="Account Twilio">
          <p className="sk-box__label">Config</p>
          <div className="sk-theme-fields">
            <label className="sk-field">
              <span>Account SID (AC…)</span>
              <input
                className="sk-input"
                value={accountSid}
                onChange={(e) => setAccountSid(e.target.value)}
                autoComplete="off"
                spellCheck={false}
              />
            </label>
            <label className="sk-field">
              <span>Messaging Service SID (MG…)</span>
              <input
                className="sk-input"
                value={messagingSid}
                onChange={(e) => setMessagingSid(e.target.value)}
                autoComplete="off"
                spellCheck={false}
              />
            </label>
            <label className="sk-field">
              <span>Auth Token</span>
              <input
                className="sk-input"
                type="password"
                autoComplete="new-password"
                value={authToken}
                placeholder={
                  status.has_auth_token && !tokenEdited
                    ? '•••••••• (token già salvato — lascia vuoto per mantenere)'
                    : 'Incolla Auth Token (non un SID)'
                }
                onChange={(e) => {
                  setAuthToken(e.target.value);
                  setTokenEdited(true);
                }}
              />
            </label>
          </div>
          <p className="sk-muted" style={{ marginTop: '0.75rem' }}>
            Non usare SID (AC/VA/MG/SK) nel campo Auth Token. Se il ciphertext non è decifrabile,
            allinea MASTER_EMAIL_SETTINGS_SECRET oppure incolla un nuovo token.
          </p>
        </SectionCard>
      </div>
    </section>
  );
}
